import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionInfo {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  status: string | null;
  trialEnd: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  subscription: SubscriptionInfo;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const defaultSub: SubscriptionInfo = {
  subscribed: false,
  productId: null,
  subscriptionEnd: null,
  status: null,
  trialEnd: null,
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  subscription: defaultSub,
  checkSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(defaultSub);

  const checkSubscription = useCallback(async () => {
    try {
      // Ensure session token is attached before invoking; avoids race-condition 401s.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setSubscription(defaultSub);
        return;
      }
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) {
        console.error("check-subscription error:", error);
        return;
      }
      setSubscription({
        subscribed: data?.subscribed ?? false,
        productId: data?.product_id ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        status: data?.status ?? null,
        trialEnd: data?.trial_end ?? null,
      });
    } catch (e) {
      console.error("check-subscription failed:", e);
    }
  }, []);

  const ensureProfile = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase.from("profiles").select("id").eq("id", userId).maybeSingle();
      if (!data) {
        await supabase.from("profiles").insert({ id: userId });
      }
      // Rolle wird automatisch durch den DB-Trigger handle_new_user() gesetzt
    } catch (e) {
      console.error("ensureProfile failed:", e);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setLoading(false);
        if (session) {
          setTimeout(() => {
            ensureProfile(session.user.id);
            checkSubscription();
          }, 0);
        } else {
          setSubscription(defaultSub);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session) {
        ensureProfile(session.user.id);
        checkSubscription();
      }
    });

    return () => authSub.unsubscribe();
  }, [checkSubscription, ensureProfile]);

  // Periodic refresh every 60s
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, subscription, checkSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
